import { useRef, useState } from "react";
import { Link } from "react-router";
import { Check, ShoppingCart } from "lucide-react";
import type { CatalogueListing } from "../../api/catalogue";
import { Button, Icon, QuantityStepper } from "../ui";
import { cx } from "../ui/cx";
import { quantityUnit } from "./units";

export interface AddToBasketState {
  adding: boolean;
  /** How many were just added, once the basket has them. */
  added: number | null;
  error?: string;
}

export interface AddToBasketProps {
  listing: Pick<CatalogueListing, "name" | "unit" | "in_stock">;
  onAdd: (quantity: number) => void;
  state: AddToBasketState;
  /** Phone sizing: 48px controls, full width. */
  touch?: boolean;
}

/** How many, and into the basket. Out of stock is never orderable (PRD 5.7): no control at all. */
export function AddToBasket({ listing, onAdd, state, touch = false }: AddToBasketProps) {
  const [quantity, setQuantity] = useState(1);
  // Clicking Add blurs the box, which commits a typed quantity through onChange — but this
  // handler's `quantity` closure is still the pre-blur value. The ref holds the committed
  // number so Add uses what was typed, not the old one (KALI-25).
  const latest = useRef(quantity);
  const change = (next: number) => {
    latest.current = next;
    setQuantity(next);
  };
  if (!listing.in_stock) {
    return (
      <Button icon={ShoppingCart} disabled size={touch ? "lg" : "md"} fullWidth={touch}>
        Out of stock
      </Button>
    );
  }
  return (
    <div className={cx("flex flex-col gap-3", touch && "w-full")}>
      <div className="flex flex-wrap items-center gap-3">
        <QuantityStepper value={quantity} unit={quantityUnit(listing.unit, quantity)} onChange={change} size={touch ? "lg" : "md"} label={listing.name} />
        <Button icon={ShoppingCart} size={touch ? "lg" : "md"} disabled={state.adding} onClick={() => onAdd(latest.current)} className={touch ? "flex-1" : undefined}>
          {state.adding ? "Adding…" : "Add to basket"}
        </Button>
      </div>
      {state.added !== null && !state.adding && (
        <p role="status" className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 type-small text-success-700">
          <span className="inline-flex items-center gap-1">
            <Icon icon={Check} size={16} /> Added {state.added} {quantityUnit(listing.unit, state.added)}.
          </span>
          <Link to="/cart" className="type-label">
            View basket
          </Link>
        </p>
      )}
      {state.error && (
        <p role="alert" className="m-0 type-small text-danger-700">
          {state.error}
        </p>
      )}
    </div>
  );
}
